import { useState } from 'react';
import type { ReactNode } from 'react';
import type { CoreDepartment, CoreOrganization, UserLabel } from '@/lib/zifekCore';
import { createCoreDepartment, setCoreDepartmentStatus, updateCoreDepartment } from '@/lib/zifekCore';
import Modal from './Modal';

interface Props {
  org: CoreOrganization;
  departments: CoreDepartment[];
  users: Record<number, UserLabel>;
  onChanged: () => void;
}

interface FormState {
  id: number | null;
  name: string;
  description: string;
  parentDepartmentId: number | null;
  managerUserId: number | null;
}

const EMPTY: FormState = { id: null, name: '', description: '', parentDepartmentId: null, managerUserId: null };
const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-foreground-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

export default function DepartmentsManager({ org, departments, users, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState('');

  const memberList = Object.values(users);

  const openCreate = () => { setForm(EMPTY); setError(''); setOpen(true); };

  const openEdit = (d: CoreDepartment) => {
    setForm({
      id: d.id,
      name: d.name,
      description: d.description || '',
      parentDepartmentId: d.parent_department_id,
      managerUserId: d.manager_user_id,
    });
    setError('');
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Le nom du département est obligatoire.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        tenantId: org.tenant_id,
        organizationId: org.id,
        name: form.name.trim(),
        description: form.description.trim() || null,
        parentDepartmentId: form.parentDepartmentId,
        managerUserId: form.managerUserId,
      };
      if (form.id) await updateCoreDepartment(form.id, payload);
      else await createCoreDepartment(payload);
      setOpen(false);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (d: CoreDepartment) => {
    setBusyId(d.id);
    try {
      await setCoreDepartmentStatus(d.id, d.status === 'inactive' ? 'active' : 'inactive');
      onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const nameById = (id: number | null) => departments.find((d) => d.id === id)?.name || null;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-500">
          Les départements de <strong className="text-foreground-700">{org.commercial_name || org.legal_name}</strong> (Administration, Finance, Commercial, Support…).
        </p>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Nouveau département
        </button>
      </div>

      {departments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-node-tree text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucun département pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {departments.map((d) => {
            const inactive = d.status === 'inactive';
            const manager = d.manager_user_id ? users[d.manager_user_id] : undefined;
            const parent = nameById(d.parent_department_id);
            return (
              <div key={d.id} className={`bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col ${inactive ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 text-accent-900 flex items-center justify-center flex-shrink-0">
                      <i className="ri-node-tree text-lg"></i>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 truncate">{d.name}</div>
                      <div className="text-[11px] text-foreground-400 truncate">
                        {parent ? `Rattaché à ${parent}` : 'Département principal'}
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${inactive ? 'bg-foreground-100 text-foreground-600' : 'bg-accent-100 text-accent-900'}`}>
                    {inactive ? 'Désactivé' : 'Actif'}
                  </span>
                </div>
                {d.description && <p className="text-xs text-foreground-500 mb-3 line-clamp-2">{d.description}</p>}
                <div className="text-xs text-foreground-500 flex items-center gap-1.5 mb-4">
                  <i className="ri-user-star-line text-foreground-400"></i>
                  <span>{manager?.name || 'Responsable non défini'}</span>
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-background-200/60 mt-auto">
                  <button
                    onClick={() => openEdit(d)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line"></i> Modifier
                  </button>
                  <button
                    onClick={() => toggleStatus(d)}
                    disabled={busyId === d.id}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer disabled:opacity-50 ${inactive ? 'bg-accent-100 text-accent-900' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}
                  >
                    <i className={inactive ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
                    {inactive ? 'Activer' : 'Désactiver'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier le département' : 'Nouveau département'}
          onClose={() => setOpen(false)}
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                Enregistrer
              </button>
            </>
          }
        >
          {error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
          <div className="space-y-3">
            <Field label="Nom du département *">
              <input className={INPUT} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Commercial" />
            </Field>
            <Field label="Description">
              <textarea className={`${INPUT} min-h-[70px]`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Rôle de ce département…" />
            </Field>
            <Field label="Département parent (facultatif)">
              <select
                className={INPUT}
                value={form.parentDepartmentId ?? ''}
                onChange={(e) => setForm({ ...form, parentDepartmentId: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">Aucun (département principal)</option>
                {departments.filter((d) => d.id !== form.id).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Responsable">
              <select
                className={INPUT}
                value={form.managerUserId ?? ''}
                onChange={(e) => setForm({ ...form, managerUserId: e.target.value ? Number(e.target.value) : null })}
              >
                <option value="">Non défini</option>
                {memberList.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.user_name || `#${u.id}`}</option>
                ))}
              </select>
            </Field>
          </div>
        </Modal>
      )}
    </div>
  );
}